import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { CLUB_CATALOG } from "../lib/clubs";
import { api } from "../lib/api";
import { useState } from "react";

/**
 * Bottom-sheet picker that pops up after the user logs a stroke and the GPS
 * confirms a sensible carry distance. The player can pick which club they
 * used (one tap → POST /api/shots) or dismiss without saving.
 *
 * Props:
 *   open: boolean
 *   carry: number (yards)
 *   holeNumber: number
 *   roundId: string
 *   onClose: () => void
 *   onSaved: () => void
 */
export default function ShotClubPicker({ open, carry, holeNumber, roundId, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // First-time onboarding banner — explains the Bag IQ pipeline so users
  // know WHY we're asking which club they just hit. Auto-dismisses once
  // they pick a club. Sticky per device.
  const seenIntro = (() => {
    try { return localStorage.getItem("bagIqShotIntroSeen") === "1"; }
    catch { return false; }
  })();
  const markIntroSeen = () => {
    try { localStorage.setItem("bagIqShotIntroSeen", "1"); } catch (_e) { /* no-op */ }
  };

  const handlePick = async (clubKey) => {
    if (saving) return;
    setSaving(true);
    setErr("");
    try {
      await api.post("/shots", {
        round_id: roundId,
        hole_number: holeNumber,
        club_key: clubKey,
        carry_yds: carry,
      });
      markIntroSeen();
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[55] flex items-end justify-center"
          style={{ background: "rgba(20,61,47,0.55)" }}
          data-testid="shot-club-picker"
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-t-3xl pt-4 pb-6 px-5 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="label-uppercase">Detected shot</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono-num font-black text-3xl text-[var(--primary)]" data-testid="shot-picker-carry">
                    {carry}
                  </span>
                  <span className="text-sm font-bold opacity-70">yds carry</span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Skip"
                data-testid="shot-picker-close"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--text-secondary)]/10"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              Which club did you hit? <span className="opacity-60">(tap to log · skip if not a full swing)</span>
            </p>

            {!seenIntro && (
              <div
                className="rounded-xl bg-[var(--accent-turf)]/30 border border-[var(--primary)]/15 p-3 mb-3"
                data-testid="bagiq-intro"
              >
                <div className="flex items-start gap-2">
                  <div className="h-7 w-7 rounded-lg bg-[var(--primary)] text-white flex items-center justify-center shrink-0 text-xs font-black">
                    IQ
                  </div>
                  <div className="text-[11px] text-[var(--text)] leading-snug">
                    <strong className="text-[var(--primary)]">Bag IQ</strong> learns your real club distances in the background.
                    After 5+ shots per club, the Smart Caddie chip on the round screen swaps to <em>your</em> yardages instead of generic ones.
                    <br />
                    <span className="opacity-70">Just tap which club you just hit. Skip if it was a chip / punch / not a full swing.</span>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2" data-testid="shot-picker-clubs">
              {CLUB_CATALOG.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => handlePick(c.key)}
                  disabled={saving}
                  data-testid={`shot-pick-${c.key}`}
                  className="surface-card-flat py-3 px-2 text-center hover:bg-[var(--accent-turf)] transition-colors active:scale-95"
                >
                  <div className="font-heading font-bold text-[var(--primary)] text-sm">
                    {c.label}
                  </div>
                  <div className="text-[10px] text-[var(--text-secondary)] font-mono-num">
                    avg {c.default}
                  </div>
                </button>
              ))}
            </div>

            {err && (
              <p className="text-xs text-[var(--status-bad)] mt-3 text-center" data-testid="shot-picker-err">{err}</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
